import { Injectable } from '@angular/core';
import {Client, ClientList} from './client';
import {BaseService} from '../base/base.service';
import {ClientSerializer} from 'src/app/clients/client.serializer';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {filter, map, take} from 'rxjs/operators';
import {InvoiceSerializer} from '../invoices/invoice.serializer';
import {Observable} from 'rxjs';
import {Invoice} from '../invoices/invoice';
import {Group} from '../filter/group';
import {Filter} from '../filter/filter';
import {Predicate} from '../shared/enums/predicate.enum';
import {Page} from '../shared/models/page';

export interface TerminationMetrics {
  pipedrive_deal_id: number;
  contract_started_on: string;
  next_contract_anniversary: string;
  partners: {id: number, name: string, memberships_count: number, transactions_count: number}[];
  businesses: {id: number, name: string, partner_id: number, last_transaction_date: string, disabled_at: string}[];
  contract: {id: number, package: string, contract_term: string};
}

export interface TerminationInvoicePreview {
  invoice_id: number;
  period_start: string;
  items: {id: number, name: string, recurring_rule: string, ends_at_before: string, ends_at_after: string,
    action: 'end' | 'keep' | 'check_yearly'}[];
}

export interface TerminationResult {
  invoice: TerminationInvoicePreview;
  pipedrive_error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ClientService extends BaseService<Client, ClientList> {

  constructor(http: HttpClient, private invoiceSerializer: InvoiceSerializer) {
    super(http);
    this.endpoint = 'clients' ;
    this.serializer = new ClientSerializer();
  }

  fetchNextInvoice(id: number): Observable<Invoice> {
    return this.http.get<Invoice>(`${environment.base_url + this.endpoint}/${id}/next_invoice`).pipe(
      filter(x => !!x),
      take(1),
      map((res) => this.invoiceSerializer.fromJson(res))
    );
  }

  searchByName(term: string, limit = 10): Observable<Client[]> {
    const group = new Group({
      m: 'and',
      filter: term.split(/[\s,;]+/).filter(Boolean).map(word => new Filter({subject: 'name', predicate: Predicate.CONTAINS, object1: word}))
    });
    const page = Object.assign(new Page(), {offset: 0, size: limit});

    return this.fetchPage(page, [group], [{name: 'name', dir: 'asc'}], 'id,name,company,contact,zip,city').pipe(
      take(1),
      map(list => list.data)
    );
  }

  send_welcome_mail(id: number) {
    return this.http.post<void>(`${environment.base_url + this.endpoint}/${id}/send_welcome_mail`, null);
  }

  fetchTerminationMetrics(id: number): Observable<TerminationMetrics> {
    return this.http.get<TerminationMetrics>(`${environment.base_url + this.endpoint}/${id}/termination_metrics`);
  }

  fetchTerminationInvoicePreview(id: number, effectiveOn: string): Observable<TerminationInvoicePreview> {
    return this.http.get<TerminationInvoicePreview>(`${environment.base_url + this.endpoint}/${id}/termination_invoice_preview`,
      {params: {effective_on: effectiveOn}});
  }

  scheduleTerminationCall(id: number): Observable<void> {
    return this.http.post<void>(`${environment.base_url + this.endpoint}/${id}/termination_call`, null);
  }

  executeTermination(id: number, effectiveOn: string): Observable<TerminationResult> {
    return this.http.post<TerminationResult>(`${environment.base_url + this.endpoint}/${id}/termination`,
      {effective_on: effectiveOn});
  }

}
